import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { normalizeApiError } from '@/features/common/apiError';
import { useGetOrganizationQuery } from '@/features/orgs/api/orgApi';
import { useBindNumberMutation } from '@/features/numbers/api/numbersApi';

import {
  useAttachMemoryMutation,
  useAttachPoliciesMutation,
  useCreateFromTemplateMutation,
  useCreateTriggerMutation,
  useGetModelCollectionsQuery,
  useGetTemplateQuery,
} from '../api/agentsApi';
import { kindLabel } from '../labels';
import type {
  AttachItemState,
  CreateFactoryTriggerBody,
  StepErrors,
  SubsetDenial,
  TriggerDraft,
  WizardDraft,
} from '../types';
import { agentErrorMessage, parseSubsetDenial, toExtraGrantsSpec } from '../utils';

import { AttachProgressList } from './AttachProgressList';
import { BasicsStep } from './BasicsStep';
import {
  applyStepChange,
  collectBindNumberIntents,
  collectCoderConfigOverride,
  collectForwardingOverride,
  composeWizardEntries,
  formatStepSummary,
  initWizardDraft,
  isWizardDirty,
  missingRequiredStepTypes,
  REVIEW_SUMMARY_STEP_TYPES,
  stepValue,
  type WizardEntry,
} from './draft';
import { ModelStep } from './ModelStep';
import { ReviewStep } from './ReviewStep';
import { StepRail } from './StepRail';
import { stepRegistry } from './steps/registry';
import { UnknownStepPlaceholder } from './UnknownStepPlaceholder';
import { WebhookRevealPanel } from './WebhookRevealPanel';

function triggerBody(item: TriggerDraft): CreateFactoryTriggerBody {
  if (item.kind === 'cron') {
    return { kind: 'cron', cron: item.cron };
  }
  if (item.kind === 'event') {
    return {
      kind: 'event',
      eventFilter: [{ channel: item.channel ?? '', eventTypes: item.eventTypes ?? [] }],
    };
  }
  return { kind: 'webhook' };
}

interface CreateFailure {
  denial: SubsetDenial | null;
  message?: string;
}

/**
 * Agent creation wizard (design §7, §15, §16, §17) — route
 * `/organizations/:orgId/agents/new/:templateId`.
 *
 * Frame: [basics, model, …template steps verbatim (registry-rendered), review].
 * Unknown step types render the frame-owned placeholder (forward-compat for
 * P4/P5); required+unknown disables Create with the real reason (§16). Attach
 * intents (memory / policies / triggers) and the `bind-number` intent are
 * collected in the draft and run as a VISIBLE, retryable post-create sequence
 * — partial failure never loses the agent (P4, E1); the `forwarding` step
 * merges into create `overrides.forwarding` (OQ-3) and every registered
 * generic step value surfaces on Review (§25 Q2). Subset denials render inline
 * with the exact (action, resource) pairs (§13.3). All entered data survives
 * every failure (E1).
 */
export function AgentWizardPage() {
  const { orgId = '', templateId = '' } = useParams<{ orgId: string; templateId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') ?? undefined;
  const navigate = useNavigate();
  const toast = useToast();

  const templateQuery = useGetTemplateQuery(templateId);
  const template = templateQuery.data;
  const { data: modelCollections } = useGetModelCollectionsQuery();
  const { data: organization } = useGetOrganizationQuery(orgId);

  const [draft, setDraft] = useState<WizardDraft | null>(null);
  const [entryIndex, setEntryIndex] = useState(0);
  const [stepErrors, setStepErrors] = useState<Record<string, StepErrors>>({});
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<CreateFailure | null>(null);
  const [factoryId, setFactoryId] = useState<string | null>(null);
  const [attachItems, setAttachItems] = useState<AttachItemState[]>([]);
  const [revealedWebhookUrls, setRevealedWebhookUrls] = useState<string[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const pendingNavRef = useRef<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [createFromTemplate] = useCreateFromTemplateMutation();
  const [attachMemory] = useAttachMemoryMutation();
  const [attachPolicies] = useAttachPoliciesMutation();
  const [createTrigger] = useCreateTriggerMutation();
  // bind-number intent executor (design §12.2) — reuses the numbers mutation
  // so its cache invalidation stays owned by that feature (import only).
  const [bindNumber] = useBindNumberMutation();

  useEffect(() => {
    if (template && !draft) {
      setDraft(initWizardDraft(template, projectId));
    }
  }, [template, draft, projectId]);

  const entries = useMemo(() => (template ? composeWizardEntries(template) : []), [template]);

  const current: WizardEntry | undefined = entries[entryIndex];
  const currentKey = current?.key ?? '';

  useEffect(() => {
    // Focus moves to the step heading on navigation (design §7.2, C6).
    headingRef.current?.focus();
  }, [currentKey]);

  const detailPath = (id: string) => `/organizations/${orgId}/agents/${id}`;
  const agentsBase = `/organizations/${orgId}/agents`;
  const galleryBase = `${agentsBase}/new${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;

  // ---------------------------------------------------------------------
  // Gating
  // ---------------------------------------------------------------------

  const isUnknown = (entry: WizardEntry) =>
    entry.kind === 'template' && (entry.forceUnknown || !stepRegistry.has(entry.step.type));

  const missingRequired = useMemo(
    () => missingRequiredStepTypes(entries, stepRegistry.has),
    [entries],
  );

  const currentErrors = (stepErrors[currentKey] ?? {}) as StepErrors;
  const setCurrentErrors = (next: StepErrors) =>
    setStepErrors((prev) => ({ ...prev, [currentKey]: next }));

  const stepGate = (): { disabled: boolean; reason?: string } => {
    if (!draft || !current) {
      return { disabled: true };
    }
    if (current.kind === 'frame' && current.key === 'basics' && draft.basics.name.trim() === '') {
      return { disabled: true, reason: 'Enter a name to continue' };
    }
    if (current.kind === 'frame' && current.key === 'model' && !draft.model.modelCollectionId) {
      return { disabled: true, reason: 'Pick a model collection to continue' };
    }
    if (
      current.kind === 'template' &&
      !isUnknown(current) &&
      Object.keys(currentErrors).length > 0
    ) {
      return { disabled: true, reason: 'Fix the highlighted fields to continue' };
    }
    return { disabled: false };
  };

  const reviewGate = (): { disabled: boolean; reason?: string } => {
    if (!draft) {
      return { disabled: true };
    }
    if (missingRequired.length > 0) {
      return { disabled: true, reason: "A required step isn't available yet." };
    }
    if (draft.basics.name.trim() === '') {
      return { disabled: true, reason: 'Enter a name to continue' };
    }
    if (!draft.model.modelCollectionId) {
      return { disabled: true, reason: 'Pick a model collection to continue' };
    }
    return { disabled: false };
  };

  // ---------------------------------------------------------------------
  // Post-create deferred-attach sequence (design §15.3, P4 principle)
  // ---------------------------------------------------------------------

  const buildSequenceItems = (): AttachItemState[] => {
    if (!draft) {
      return [];
    }
    const items: AttachItemState[] = [
      { id: 'create', label: 'Create agent', status: 'done', outcome: 'Agent created.' },
    ];
    const bindIntents = collectBindNumberIntents(draft, entries);
    for (const entry of entries) {
      if (entry.kind !== 'template' || isUnknown(entry)) {
        continue;
      }
      switch (entry.step.type) {
        case 'attach-storage':
          if (draft.memory.attach) {
            items.push({ id: `${entry.key}:memory`, label: 'Attach memory', status: 'pending' });
          }
          break;
        case 'attach-policy':
          if (draft.policies.statements.length > 0) {
            items.push({
              id: `${entry.key}:policies`,
              label: 'Attach policies',
              status: 'pending',
            });
          }
          break;
        case 'attach-webhook':
          if (draft.triggers.items.length > 0) {
            items.push({
              id: `${entry.key}:triggers`,
              label: 'Attach triggers',
              status: 'pending',
            });
          }
          break;
        case 'bind-number':
          if (bindIntents.some((intent) => intent.entryKey === entry.key)) {
            items.push({
              id: `${entry.key}:bind-number`,
              label: 'Bind phone number',
              status: 'pending',
            });
          }
          break;
        case 'forwarding':
          // Consumed at create via `overrides.forwarding` (OQ-3) — no
          // post-create action belongs to this step.
          break;
        default:
          break;
      }
    }
    return items;
  };

  const failureToastCopy = (itemId: string): string => {
    if (itemId.endsWith(':memory')) {
      return "Couldn't attach memory. You can retry on the agent page.";
    }
    if (itemId.endsWith(':policies')) {
      return "Couldn't attach policies. You can retry on the agent page.";
    }
    if (itemId.endsWith(':bind-number')) {
      return "Couldn't bind the phone number. You can retry on the agent page.";
    }
    return "Couldn't attach triggers. You can retry on the agent page.";
  };

  const runAttachItem = useCallback(
    async (id: string, item: AttachItemState): Promise<AttachItemState> => {
      const kind = item.id.slice(item.id.lastIndexOf(':') + 1);
      let cursor = item.cursor ?? 0;
      try {
        if (kind === 'memory') {
          await attachMemory({ id }).unwrap();
          return { ...item, status: 'done', outcome: 'Memory attached.' };
        }
        if (kind === 'policies') {
          await attachPolicies({
            id,
            policySpecs: [toExtraGrantsSpec(draft?.policies.statements ?? [])],
          }).unwrap();
          return { ...item, status: 'done', outcome: 'Policies attached.' };
        }
        if (kind === 'bind-number') {
          // Item id is `${entryKey}:bind-number` (entry keys never contain
          // ':'), so the draft step value that produced this intent resolves
          // back through the prefix.
          const entryKey = item.id.slice(0, item.id.lastIndexOf(':'));
          const value = draft?.steps[entryKey] as { numberId?: unknown } | undefined;
          const numberId =
            typeof value?.numberId === 'string' && value.numberId !== '' ? value.numberId : '';
          if (!numberId) {
            return { ...item, status: 'error', outcome: 'No phone number was selected.' };
          }
          await bindNumber({ id: numberId, factoryId: id }).unwrap();
          return { ...item, status: 'done', outcome: 'Number bound.' };
        }
        const triggerItems = draft?.triggers.items ?? [];
        for (let index = cursor; index < triggerItems.length; index += 1) {
          const created = await createTrigger({
            id,
            body: triggerBody(triggerItems[index]),
          }).unwrap();
          if (created.webhookUrl) {
            setRevealedWebhookUrls((prev) => [...prev, created.webhookUrl as string]);
          }
          cursor = index + 1;
        }
        return {
          ...item,
          status: 'done',
          cursor,
          outcome: `${triggerItems.length} trigger${triggerItems.length === 1 ? '' : 's'} attached.`,
        };
      } catch (error) {
        return {
          ...item,
          status: 'error',
          cursor,
          denial: parseSubsetDenial(error) ?? undefined,
          outcome: agentErrorMessage(error, "We couldn't attach this right now. Please try again."),
        };
      }
    },
    [attachMemory, attachPolicies, bindNumber, createTrigger, draft],
  );

  const finalizeIfComplete = useCallback(
    (items: AttachItemState[], id: string) => {
      const allDone = items.every((item) => item.status === 'done');
      if (allDone && revealedWebhookUrls.length === 0) {
        // Success completion navigates to the detail page (§15.3). With
        // once-only webhook URLs still on screen we pause instead — leaving
        // would discard data the backend shows exactly once (Q1).
        navigate(detailPath(id));
      }
    },
    [navigate, revealedWebhookUrls.length],
  );

  const runSequence = useCallback(
    async (items: AttachItemState[], id: string) => {
      const working = [...items];
      for (let index = 0; index < working.length; index += 1) {
        if (working[index].status === 'done') {
          continue;
        }
        const started: AttachItemState = { ...working[index], status: 'running' };
        working[index] = started;
        setAttachItems([...working]);
        const finished = await runAttachItem(id, started);
        working[index] = finished;
        setAttachItems([...working]);
        if (finished.status === 'error') {
          toast.error(failureToastCopy(finished.id));
        }
      }
      finalizeIfComplete(working, id);
    },
    [finalizeIfComplete, runAttachItem, toast],
  );

  const handleRetryItem = async (itemId: string) => {
    if (!factoryId || itemId === 'create') {
      return;
    }
    const index = attachItems.findIndex((item) => item.id === itemId);
    if (index < 0) {
      return;
    }
    const working = [...attachItems];
    const started: AttachItemState = { ...working[index], status: 'running' };
    working[index] = started;
    setAttachItems(working);
    const finished = await runAttachItem(factoryId, started);
    working[index] = finished;
    setAttachItems([...working]);
  };

  // ---------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------

  const handleCreate = async () => {
    if (!draft || creating || factoryId) {
      return;
    }
    const gate = reviewGate();
    if (gate.disabled) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      // Registered-step merge (OQ-3): the `forwarding` step's persisted value
      // rides into create as `overrides.forwarding`; absent until touched, so
      // the template default stays authoritative. P6-T3: the coder steps'
      // values (select-image / attach-repository / attach-secrets) ride in as
      // `overrides.coderConfig` and persist onto the factory.
      const forwarding = collectForwardingOverride(draft, entries);
      const coderConfig = collectCoderConfigOverride(draft, entries);
      const result = await createFromTemplate({
        templateId,
        name: draft.basics.name.trim(),
        orgId,
        ...(draft.projectId ? { projectId: draft.projectId } : {}),
        overrides: {
          instructions: draft.basics.instructions,
          modelCollectionId: draft.model.modelCollectionId ?? undefined,
          ...(forwarding ? { forwarding } : {}),
          ...(coderConfig ? { coderConfig } : {}),
        },
      }).unwrap();
      const createdFactoryId = result.factory.id;
      setFactoryId(createdFactoryId);
      toast.success('Agent created.');
      const items = buildSequenceItems();
      setAttachItems(items);
      await runSequence(items, createdFactoryId);
    } catch (error) {
      const denial = parseSubsetDenial(error);
      setCreateError({
        denial,
        message: denial ? undefined : agentErrorMessage(error, "Couldn't create this agent."),
      });
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------------
  // Navigation + discard guard
  // ---------------------------------------------------------------------

  const dirty = Boolean(draft && template && isWizardDirty(draft, template));

  const guardedNavigate = (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (dirty && !factoryId) {
      event.preventDefault();
      pendingNavRef.current = to;
      setDiscardOpen(true);
    }
  };

  const confirmDiscard = () => {
    setDiscardOpen(false);
    const target = pendingNavRef.current;
    pendingNavRef.current = null;
    if (target) {
      navigate(target);
    }
  };

  // Completed rail items are revisitable (design §7.2 — draft is preserved).
  const jumpToStep = (index: number) => {
    setEntryIndex(Math.min(Math.max(0, index), entries.length - 1));
  };

  const policyStepIndex = entries.findIndex(
    (entry) => entry.kind === 'template' && entry.step.type === 'attach-policy',
  );

  const advance = () => {
    if (!current || entryIndex >= entries.length - 1) {
      return;
    }
    const gate = stepGate();
    if (gate.disabled) {
      return;
    }
    setEntryIndex((index) => index + 1);
  };

  const handlePrimary = () => {
    if (current?.kind === 'frame' && current.key === 'review') {
      if (factoryId) {
        navigate(detailPath(factoryId));
      } else {
        void handleCreate();
      }
      return;
    }
    advance();
  };

  // ---------------------------------------------------------------------
  // Views
  // ---------------------------------------------------------------------

  if (templateQuery.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <SkeletonBlock className="h-8 w-64" />
        <SkeletonBlock className="h-5 w-96" />
        <div className="flex gap-6">
          <SkeletonBlock className="h-96 w-64 shrink-0" />
          <SkeletonBlock className="h-96 flex-1" />
        </div>
      </div>
    );
  }

  if (templateQuery.isError || !template) {
    if (normalizeApiError(templateQuery.error).status === 404) {
      return (
        <EmptyState
          title="Template not found"
          description="It may have been removed."
          action={
            <Button variant="outline" onClick={() => navigate(galleryBase)}>
              Back to templates
            </Button>
          }
        />
      );
    }
    return (
      <QueryErrorView
        error={templateQuery.error}
        label="the template"
        onRetry={() => void templateQuery.refetch()}
      />
    );
  }

  if (!draft || !current) {
    return null;
  }

  const headingTitle = current.kind === 'frame' ? current.title : current.step.title;
  const StepComponent =
    current.kind === 'template' && !current.forceUnknown
      ? stepRegistry.get(current.step.type)
      : undefined;
  const review = current.kind === 'frame' && current.key === 'review';
  const inSequence = review && factoryId !== null;

  const gate = review ? reviewGate() : stepGate();
  const primaryLabel = review ? (creating ? 'Creating agent…' : 'Create agent') : 'Continue';

  // Review summary rows (P5 §25 Q2): every registered generic-store step that
  // holds a value — text/select verbatim, forwarding/bind-number shaped from
  // their draft values, coder steps via their built-in `summary`. Named-slice
  // types get dedicated rows above and are excluded here.
  const advisoryRows = entries
    .filter(
      (entry): entry is Extract<WizardEntry, { kind: 'template' }> =>
        entry.kind === 'template' &&
        !entry.forceUnknown &&
        REVIEW_SUMMARY_STEP_TYPES.includes(entry.step.type),
    )
    .map((entry) => ({
      title: entry.step.title,
      value: formatStepSummary(entry.step.type, draft.steps[entry.key]),
    }))
    .filter((row): row is { title: string; value: string } => row.value !== undefined);

  const modelCollection = (modelCollections ?? []).find(
    (collection) => collection.id === draft.model.modelCollectionId,
  );

  const breadcrumbItems: { label: string; to?: string }[] = [
    {
      label: organization?.name ?? 'Organization',
      to: `/organizations/${orgId}`,
    },
    { label: 'Agents', to: agentsBase },
    { label: 'New agent', to: `${agentsBase}/new` },
    { label: template.name },
  ];

  return (
    <section aria-labelledby="wizard-title" className="space-y-6">
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            return (
              <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
                {index > 0 ? (
                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : null}
                {isLast || !item.to ? (
                  <span
                    className={isLast ? 'truncate font-medium text-foreground' : 'truncate'}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    onClick={guardedNavigate(item.to)}
                    className="truncate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <PageHeader
        title="Create agent"
        description={`From template “${template.name}”${template.description ? ` — ${template.description}` : ''}`}
        actions={
          <Badge variant="secondary" className="rounded-sm">
            {kindLabel(template.kind)}
          </Badge>
        }
        titleAs="h1"
        titleId="wizard-title"
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <StepRail entries={entries} currentIndex={entryIndex} onJump={jumpToStep} />

        <div className="min-w-0 flex-1 space-y-6">
          {/* <lg: compact progress header (design §23) */}
          <div className="space-y-2 lg:hidden">
            <p className="text-sm font-medium">
              Step {entryIndex + 1} of {entries.length} — {headingTitle}
            </p>
            <div aria-hidden="true" className="h-1 w-full rounded bg-muted">
              <div
                className="h-1 rounded bg-primary transition-all motion-reduce:transition-none"
                style={{ width: `${((entryIndex + 1) / entries.length) * 100}%` }}
              />
            </div>
          </div>

          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-lg font-semibold tracking-tight focus-visible:outline-none"
          >
            {headingTitle}
          </h2>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!gate.disabled) {
                handlePrimary();
              }
            }}
            className="space-y-6"
          >
            {inSequence ? (
              <div className="space-y-4">
                <p className="text-sm font-medium">Setting up your agent</p>
                <AttachProgressList
                  items={attachItems}
                  onRetry={(itemId) => void handleRetryItem(itemId)}
                  onEditGrants={
                    policyStepIndex >= 0 ? () => setEntryIndex(policyStepIndex) : undefined
                  }
                />
                <WebhookRevealPanel urls={revealedWebhookUrls} />
              </div>
            ) : current.kind === 'frame' && current.key === 'basics' ? (
              <BasicsStep
                basics={draft.basics}
                onChange={(basics) => setDraft({ ...draft, basics })}
                errors={(stepErrors.basics ?? {}) as StepErrors}
                onErrors={(next) => setStepErrors((prev) => ({ ...prev, basics: next }))}
                templateName={template.name}
                hasDefaultInstructions={Boolean(template.defaultInstructions)}
              />
            ) : current.kind === 'frame' && current.key === 'model' ? (
              <ModelStep
                selectedId={draft.model.modelCollectionId}
                onSelect={(modelCollectionId) =>
                  setDraft({ ...draft, model: { modelCollectionId } })
                }
              />
            ) : review ? (
              <ReviewStep
                template={template}
                draft={draft}
                modelCollection={modelCollection}
                missingRequired={missingRequired}
                createError={createError}
                onEditBasics={() => setEntryIndex(0)}
                advisoryRows={advisoryRows}
              />
            ) : current.kind === 'template' && StepComponent ? (
              <StepComponent
                step={current.step}
                template={template}
                value={stepValue(draft, current)}
                onChange={(next) => setDraft(applyStepChange(draft, current, next))}
                errors={currentErrors}
                onErrors={setCurrentErrors}
                orgId={orgId}
              />
            ) : current.kind === 'template' ? (
              <UnknownStepPlaceholder step={current.step} />
            ) : null}

            {!inSequence ? (
              <div className="flex items-center justify-between gap-2 border-t pt-6">
                {entryIndex > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setEntryIndex((index) => Math.max(0, index - 1))}
                  >
                    Back
                  </Button>
                ) : (
                  <span />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        type="submit"
                        disabled={gate.disabled || creating}
                        aria-describedby={gate.reason ? 'wizard-primary-reason' : undefined}
                      >
                        {creating ? (
                          <LoadingSpinner size="sm" label="Creating agent" className="mr-2" />
                        ) : null}
                        {primaryLabel}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {gate.reason ? (
                    <TooltipContent id="wizard-primary-reason" className="max-w-xs">
                      {gate.reason}
                    </TooltipContent>
                  ) : null}
                </Tooltip>
              </div>
            ) : (
              // The sequence never blocks departure — the agent exists (§15.3):
              // one primary, enabled the moment the factory does. Step revision
              // stays available through the (completed) rail.
              <div className="flex items-center justify-end gap-2 border-t pt-6">
                <Button type="button" onClick={() => navigate(detailPath(factoryId))}>
                  Go to agent
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this agent?"
        description="If you leave now, your changes will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={confirmDiscard}
      />
    </section>
  );
}
